import { FundNature, BrowseTabId, HYBRID_CATEGORY_IDS, BrowseCategoriesResponse } from '../types/peer-analyser'

const DEBT_CATEGORY_IDS = [
  'BANKING_AND_PSU',
  'CORPORATE_BOND',
  'CREDIT_RISK',
  'DYNAMIC_BOND',
  'FLOATING_RATE',
  'GOVERNMENT_BOND',
  'LIQUID',
  'LONG_DURATION',
  'MEDIUM_DURATION',
  'LOW_DURATION',
  'MEDIUM_TO_LONG_DURATION',
  'SHORT_DURATION',
  'ULTRA_SHORT_DURATION',
  'MONEY_MARKET',
  'OVERNIGHT',
  'TEN_YR_GOVERNMENT_BOND',
  'OTHER_BOND'
]

/**
 * Get fund nature from category ID
 * @param categoryId - The category enum ID (e.g., 'LARGE_CAP', 'INDEX_LARGE_CAP', 'AGGRESSIVE_ALLOCATION')
 * @param browseCategoriesData - Optional backend browse categories response to infer from list membership
 * @returns FundNature enum value
 */
export function getFundNatureFromCategory(
  categoryId: string,
  browseCategoriesData?: BrowseCategoriesResponse
): FundNature {
  if (browseCategoriesData?.globalFundsList?.some((section) => section?.categoryType?.includes(categoryId))) {
    return FundNature.GLOBAL
  }
  if (browseCategoriesData?.fofFundsList?.some((section) => section?.categoryType?.includes(categoryId))) {
    return FundNature.FUND_OF_FUNDS
  }
  if (
    (browseCategoriesData?.debtFundsList ?? []).some((section) => (section?.categoryType ?? []).includes(categoryId))
  ) {
    return FundNature.DEBT
  }
  if (browseCategoriesData?.indexFundsList?.some((section) => section?.categoryType?.includes(categoryId))) {
    return FundNature.INDEX
  }
  if (browseCategoriesData?.commodityFundsList?.some((section) => section?.categoryType?.includes(categoryId))) {
    return FundNature.COMMODITY
  }
  if (browseCategoriesData?.solutionOrientedFundsList?.some((section) => section?.categoryType?.includes(categoryId))) {
    return FundNature.SOLUTIONS_ORIENTED
  }
  if (browseCategoriesData?.hybridFundsList?.some((section) => section?.categoryType?.includes(categoryId))) {
    return FundNature.HYBRID
  }
  if (browseCategoriesData?.equityFundsList?.some((section) => section?.categoryType?.includes(categoryId))) {
    return FundNature.EQUITY
  }

  if (categoryId.startsWith('GLOBAL_')) return FundNature.GLOBAL
  if (categoryId.startsWith('INDEX_')) return FundNature.INDEX
  if (categoryId.startsWith('COMMODITY_')) return FundNature.COMMODITY
  if (DEBT_CATEGORY_IDS.includes(categoryId)) return FundNature.DEBT

  if (categoryId === 'CHILDREN' || categoryId === 'RETIREMENT') return FundNature.SOLUTIONS_ORIENTED
  if (categoryId === 'FUND_OF_FUNDS') return FundNature.FUND_OF_FUNDS

  if (HYBRID_CATEGORY_IDS.includes(categoryId as any)) {
    return FundNature.HYBRID
  }
  return FundNature.EQUITY
}

/**
 * Get tab ID from fund nature
 * @param fundNature - The fund nature enum
 * @returns BrowseTabId enum value
 */
export function getTabIdFromFundNature(fundNature: FundNature): BrowseTabId {
  const mapping: Record<FundNature, BrowseTabId> = {
    [FundNature.EQUITY]: BrowseTabId.EQUITY_FUNDS,
    [FundNature.DEBT]: BrowseTabId.DEBT_FUNDS,
    [FundNature.SOLUTIONS_ORIENTED]: BrowseTabId.SOLUTIONS_ORIENTED_FUNDS,
    [FundNature.INDEX]: BrowseTabId.INDEX_FUNDS,
    [FundNature.HYBRID]: BrowseTabId.HYBRID_FUNDS,
    [FundNature.COMMODITY]: BrowseTabId.COMMODITY_FUNDS,
    [FundNature.OTHERS]: BrowseTabId.EQUITY_FUNDS,
    [FundNature.GLOBAL]: BrowseTabId.GLOBAL_FUNDS,
    [FundNature.FUND_OF_FUNDS]: BrowseTabId.FUND_OF_FUNDS
  }
  return mapping[fundNature]
}

/**
 * Get fund nature from tab ID
 * @param tabId - The tab ID enum
 * @returns FundNature enum value
 */
export function getFundNatureFromTabId(tabId: string): FundNature {
  const mapping: Record<BrowseTabId, FundNature> = {
    [BrowseTabId.EQUITY_FUNDS]: FundNature.EQUITY,
    [BrowseTabId.DEBT_FUNDS]: FundNature.DEBT,
    [BrowseTabId.INDEX_FUNDS]: FundNature.INDEX,
    [BrowseTabId.HYBRID_FUNDS]: FundNature.HYBRID,
    [BrowseTabId.COMMODITY_FUNDS]: FundNature.COMMODITY,
    [BrowseTabId.SOLUTIONS_ORIENTED_FUNDS]: FundNature.SOLUTIONS_ORIENTED,
    [BrowseTabId.GLOBAL_FUNDS]: FundNature.GLOBAL,
    [BrowseTabId.FUND_OF_FUNDS]: FundNature.FUND_OF_FUNDS
  }

  if (tabId in mapping) {
    return mapping[tabId as BrowseTabId]
  }

  // Unknown tab ids map back to equity to preserve existing behavior.
  return FundNature.EQUITY
}

/**
 * Get fund nature label for display
 * @param fundNature - The fund nature enum
 * @returns Display label string
 */
export function getFundNatureLabel(fundNature: FundNature): string {
  const labels: Record<FundNature, string> = {
    [FundNature.EQUITY]: 'Equity funds',
    [FundNature.DEBT]: 'Debt Funds',
    [FundNature.INDEX]: 'Index funds',
    [FundNature.HYBRID]: 'Hybrid funds',
    [FundNature.COMMODITY]: 'Gold & Silver Funds',
    [FundNature.SOLUTIONS_ORIENTED]: 'Solution Oriented Funds',
    [FundNature.GLOBAL]: 'Global Funds',
    [FundNature.FUND_OF_FUNDS]: 'Fund of Funds',
    [FundNature.OTHERS]: 'Other Funds'
  }
  return labels[fundNature]
}
